import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  useFonts,
} from '@expo-google-fonts/inter';
import '@expo/metro-runtime'; // this enables fast-refresh in development...what happens in production?
import '@gripp/i18n';
import {
  EnvConfig,
  Graphback,
  GrippContext,
  currentWorkspaceQueryKey,
  fetchStoredCurrentWorkspace,
  useAuthentication,
  useCurrentAccount,
  useFeatures,
  useGrippContext,
  useStorageKeys,
  useWatchLocation,
} from '@gripp/shared-logic';
import type { Workspace } from '@gripp/shared-logic';
import {
  SystemNotification,
  SystemNotificationProvider,
} from '@gripp/shared-ui';
import Clarity from '@microsoft/clarity';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useMemo, useState } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { MediaPicker } from '../shared/components/media/mediaPicker';
import { SignatureScreenWeb } from '../shared/components/signature/signatureScreenWeb';
import { localStorageHandler } from '../shared/localStorageHandler';
// Importing the hook also runs the module's App Insights init side effect.
import { useTelemetryContext } from '../shared/logging/appInsightsWeb';
import { ErrorBoundary } from '../shared/logging/errorBoundary';
import { SentryWebLogger } from '../shared/logging/sentryWebLogger';
import { CallProvider } from '../calls/CallProvider';
import { routes } from './routing';
import { ThemeManager } from './themeManager';

type AppRouter = ReturnType<typeof createBrowserRouter>;

export const App = () => {
  const [client] = useState(() => new QueryClient());
  const router = useMemo(
    () => SentryWebLogger.wrap(createBrowserRouter)(routes),
    []
  );
  const { watchLocation } = useWatchLocation(localStorageHandler);
  watchLocation(true);

  const clarityProjectId = EnvConfig.clarityProjectId;
  useEffect(() => {
    if (clarityProjectId) {
      Clarity.init(clarityProjectId);
    }
  }, [clarityProjectId]);

  const [fontsLoaded, fontsError] = useFonts({
    Inter: Inter_400Regular,
    InterMedium: Inter_500Medium,
    InterSemiBold: Inter_600SemiBold,
    InterBold: Inter_700Bold,
    TwemojiMozilla: '/TwemojiMozilla.woff2',
  });

  if (!fontsLoaded && !fontsError) {
    return null;
  }

  return (
    <GrippContext.Provider
      value={{
        logger: SentryWebLogger,
        secureStorageHandler: localStorageHandler,
        storageHandler: localStorageHandler,
        mediaPickerComponent: MediaPicker,
        signaturePadScreen: SignatureScreenWeb,
      }}
    >
      <QueryClientProvider client={client}>
        {/* Inside the provider: the preference is held in the query cache.
            Above ErrorBoundary so a render error still keeps the right theme. */}
        <ThemeManager />
        <ErrorBoundary>
          <SystemNotificationProvider>
            <AppRoutes router={router} />
            <SystemNotification />
          </SystemNotificationProvider>
        </ErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </GrippContext.Provider>
  );
};

const AppRoutes = ({ router }: { router: AppRouter }) => {
  const authentication = useAuthentication();

  if (authentication?.isAuthenticated && authentication.accessToken) {
    Graphback.initialize(authentication.accessToken);
  }

  useEffect(() => {
    if (!authentication || authentication.isLoading) {
      return;
    }

    if (!authentication.isAuthenticated || !authentication.accessToken) {
      Graphback.dispose();
    }
  }, [authentication]);

  if (!authentication || authentication.isLoading) {
    return null;
  }

  if (!authentication.isAuthenticated || !authentication.accessToken) {
    return <RouterProvider router={router} />;
  }

  return <AuthenticatedBootstrap router={router} />;
};

const AuthenticatedBootstrap = ({ router }: { router: AppRouter }) => {
  const {
    account,
    isLoading: isAccountLoading,
    isFetching: isAccountFetching,
  } = useCurrentAccount();
  const { data: storedWorkspace, isLoading: isStoredWorkspaceLoading } =
    useStoredCurrentWorkspace();
  const { isLoading: isFeaturesLoading } = useFeatures();

  const currentWorkspace = storedWorkspace ?? account?.workspace;

  useTelemetryContext(account?.id, currentWorkspace?.id);

  const isAccountPending = isAccountLoading || (!account && isAccountFetching);
  const isBootstrapPending =
    isAccountPending || isStoredWorkspaceLoading || isFeaturesLoading;

  if (isBootstrapPending) {
    return null;
  }

  return (
    <CallProvider navigate={(path) => router.navigate(path)}>
      <RouterProvider router={router} />
    </CallProvider>
  );
};

const useStoredCurrentWorkspace = () => {
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();

  return useQuery<Workspace | null>({
    staleTime: Infinity,
    queryKey: currentWorkspaceQueryKey,
    queryFn: () => fetchStoredCurrentWorkspace(storageHandler, storageKeys),
  });
};

export default App;
